import { useEffect, useState } from "react";
import { LifeBuoy, ChevronDown, CheckCircle2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function TicketRow({ t, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-slate-100" data-testid={`ticket-row-${t.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors" data-testid={`ticket-toggle-${t.id}`}>
        <span className="font-mono text-xs text-slate-400 w-12">#{t.number}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{t.name} <span className="font-normal text-slate-500">· {t.email}{t.company ? ` · ${t.company}` : ""}</span></div>
          <div className="text-xs text-slate-500 truncate">{t.message}</div>
        </div>
        <span className="hidden sm:inline tp-chip tp-chip-indigo text-[10px] capitalize">{t.topic}</span>
        <span className={`tp-chip text-[10px] ${t.status === "open" ? "tp-chip-amber" : "tp-chip-emerald"}`} data-testid={`ticket-status-${t.id}`}>{t.status}</span>
        <span className="hidden md:inline text-xs text-slate-400 w-36 text-right">{fmt(t.created_at)}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3" data-testid={`ticket-detail-${t.id}`}>
          <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{t.message}</p>
          {t.transcript?.length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto text-xs">
              {t.transcript.map((m, i) => <div key={i}><b className={m.role === "user" ? "text-slate-900" : "text-indigo-600"}>{m.role === "user" ? "Visitor" : "Sifty"}:</b> <span className="text-slate-700 whitespace-pre-wrap">{m.content}</span></div>)}
            </div>
          )}
          <div className="flex gap-2">
            <a href={`mailto:${t.email}?subject=Re: RecruitSift request %23${t.number}`} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:border-slate-300" data-testid={`ticket-reply-${t.id}`}>Reply by email</a>
            {t.status === "open"
              ? <button onClick={() => onStatus(t, "resolved")} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white flex items-center gap-1" data-testid={`ticket-resolve-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved</button>
              : <button onClick={() => onStatus(t, "open")} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 flex items-center gap-1" data-testid={`ticket-reopen-${t.id}`}><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupportInbox() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("open");
  const load = () => api.get("/support/tickets", { params: { status: filter === "all" ? undefined : filter } }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const onStatus = async (t, status) => {
    await api.patch(`/support/tickets/${t.id}`, { status });
    toast.success(status === "resolved" ? `Ticket #${t.number} resolved` : `Ticket #${t.number} reopened`);
    load();
  };
  return (
    <div className="tp-card overflow-hidden" data-testid="support-inbox">
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <LifeBuoy className="w-4 h-4 text-indigo-600" />
        <div className="font-semibold text-slate-900 text-sm flex-1">Support Inbox <span className="text-slate-400 font-normal">· from the website assistant</span></div>
        {["open", "resolved", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`support-filter-${f}`}
            className={`text-xs px-2.5 py-1 rounded-full capitalize transition-colors ${filter === f ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 font-medium" : "text-slate-600 hover:bg-slate-100"}`}>{f}</button>
        ))}
      </div>
      {rows.length === 0
        ? <div className="px-4 py-6 border-t border-slate-100 text-sm text-slate-500" data-testid="support-empty">No {filter === "all" ? "" : filter} requests yet.</div>
        : rows.map((t) => <TicketRow key={t.id} t={t} onStatus={onStatus} />)}
    </div>
  );
}
